import { Injectable, NgModule } from '@angular/core';
import { DataApiService, BaseService } from '@xpo-ltl/data-api';
import { ConfigManagerService, CONFIG_MANAGER_SERVICE_CONFIGURATION, ConfigManagerModule } from '@xpo-ltl/config-manager';
import { DataValidationError } from '@xpo-ltl/sdk-common';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes} checked by tsc
 */
import * as ɵngcc0 from '@angular/core';
import * as ɵngcc1 from '@xpo-ltl/data-api';
import * as ɵngcc2 from '@xpo-ltl/config-manager';
class LocationApiService extends BaseService {
    /**
     * @param {?} dataApiService
     * @param {?} configManager
     */
    constructor(dataApiService, configManager) {
        super();
        this.dataApiService = dataApiService;
        this.configManager = configManager;
    }
    /**
     * Health check URL. Responds with success message if the service is running.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    healthCheck(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/health-check'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * List of resources.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    options(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/options'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Details of user invoking the API.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    loggedInUser(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/appusers/logged-in-user'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Returns location information about a service center. This information shall include the service center's physical address as well as geocoordinates for use in GIS applications.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getServiceCenterDetails(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers'), {
            queryParams: request
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Returns location information about a service center. This information shall include the service center's physical address as well as geocoordinates for use in GIS applications.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getServiceCenterDetailsBySic(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}'), {
            pathParams: request
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Returns book closing SIC (could be Service Center, Area or District) based on the Oracle Financial Triplet which consists of Company Code, Profit Center Code and Department Code.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. Must have a valid Oracle Financial Triplet.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If the Oracle Financial Triplet is valid, the SIC code and name that are associated with it will be returned.
     * <br/>2. If the Oracle Financial Triplet is invalid or there is no SIC code and name associated with it, an error will be returned.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. Input Oracle Financial Triplet supplied with Company Code of 4052.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Not found error is returned.
     * <br/>2. Retry the call with the same Input Oracle Financial Triplet but with a Company Code of 4051.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getSicByFinanceCodes(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{companyCd}/{profitCenterCd}/{departmentCd}'), {
            pathParams: request
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This service retrieves all postal codes assigned to a SIC.  A valid SIC must be provided.
     * <br/>Pre-conditions:
     * <br/>	Valid input data - SIC
     * <br/>Post-conditions:
     * <br/>	A list of postal codes will be returned.
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPostalAssignmentsForSic(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/postalassignments/{sic}'), {
            pathParams: request
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * list SICs with postal assignment changes (either adds or deletes) for the week
     * <br/>pre-condition - valid SIC and valid effective date(mm/dd/yyyy). The effective date will be used minus one day to determine assignments that have expired.
     * <br/>post condition - list of SICs
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listSicsWithPostalAssignmentChanges(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/postalassignment'), {
            queryParams: request
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * List locations using input filter criteria describing the desired location results.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>Relevant search criteria will be supplied.
     * <br/>
     * <br/>Post-condition:
     * <br/>One or more location Sic codes with address details will be returned based on the input search criteria for locations with respective field equal to or like the input value.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listSics(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/sics'), {
            queryParams: request
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This service will retrieve the working or non-working daylist on the basis of given day.
     * <br/>Pre-conditions:
     * <br/>1. Input data are valid. A valid start date is provided.
     * <br/>Post-conditions:
     * <br/>1. If Input data not valid/does not exist, error message is returned.
     * <br/>2. If Input data valid/exists, user details are updated.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listOperationalCalendarDays(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/opscalendardays'), {
            queryParams: request
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This service will determine the next business day based on input date and offset number of days. The number of offset days are added to the start date and the next business day is determined after adjusting for weekends or holidays.
     * <br/>Pre-conditions:
     * <br/>1. Input data are valid. A valid start date is provided.
     * <br/>Post-conditions:
     * <br/>1. If Input data not valid/does not exist, error message is returned.
     * <br/>2. If Input data valid/exists, next business day is returned.
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getNextBusinessDay(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/nextbusinessday'), {
            queryParams: request
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * For XPO customer use. Returns information about the service center based on the sic code or services the area identified by a provided ZIP code. This information includes the service center's physical address and geo-coordinates for use in GIS applications as well as its contact information.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getCustomerServiceCenterDetails(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/customer'), {
            queryParams: request
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Validates the input address and if valid, returns a properly coded address.  Otherwise, returns appropriate error codes.
     * <br/>Pre-Condition: Mandatory address input is supplied and valid.
     * <br/>Post-Condition: Validated address is returned as a properly coded address.
     * <br/>
     * <br/>Pre-Condition: Mandatory address input is supplied and is not valid.
     * <br/>Post-Condition: Address validation result is returned with message indicating the error with the validated address.
     * <br/>
     * <br/>Possible validation results for invalid address:
     * <br/>  RETURN REASON CODE SPECIFICATION
     * <br/>  -20       2  Country Subdivision required
     * <br/>  -20       4  Postal Code required
     * <br/>  -20       5  City name required
     * <br/>  -20       6  Address 1 required
     * <br/>  -20      10  Country code required
     * <br/>  -21      30  Invalid Country Code
     * <br/>  -21      33  City contains invalid characters
     * <br/>  -21      34  Postal Code Contains leading space
     * <br/>  -21      35  Postal Code has invalid characters
     * <br/>  -21      36  Postal Code must be numeric
     * <br/>  -21      37  Postal Code invalid length for the United States
     * <br/>  -21      38  Postal Code invalid length for Canada
     * <br/>  -21      39  Postal Code invalid format for Canada.  It must be A#A#A#.
     * <br/>  -21      41  Country Subdivision contains invalid characters
     * <br/>  -21      42  Postal Code not determined
     * <br/>  -21      43  City not determined
     * <br/>  -21      44  Street not matched
     * <br/>  -21      45  Civic number not found on street, or out of range.
     * <br/>  -21      46  Street name not found in postal code/municipality.
     * <br/>  -21      47  Postal code and municipality not found in database.
     * <br/>  -21      48  No address match was found, and the municipality/
     * <br/>               province does not agree with the original postal code.
     * <br/>  -21      49  No address match was found, but the municipality/
     * <br/>               province agrees with the original postal code.
     * <br/>  ===================================================================
     * <br/>
     * <br/>  RETURN REASON CODE SPECIFICATION
     * <br/>  -30       1  ZIP+4 Must be Blank for CA
     * <br/>  -30       2  Carrier route not determined
     * <br/>  -30       3  ZIP+4 not determined
     * <br/>  -30       4  Range not determined
     * <br/>  -30       5  Suffix/directional could not be determined
     * <br/>  -30       6  Input street could not be matched
     * <br/>  -30       7  Firm processing was not successful - there was
     * <br/>               information on the firm line (FR) but it could not be
     * <br/>               matched to the address
     * <br/>  -30       8  Secondary address error
     * <br/>  -30       9  Non-conventional address not determined
     * <br/>  ===================================================================
     * <br/>  NOTE
     * <br/>  ===================================================================
     * <br/>  RETURN/REASON CODES
     * <br/>  -30      10  City type not determined
     * <br/>  -30      11  Address type not determined
     * <br/>  -30      12  Delivery type not determined
     * <br/>  -30      13  All address lines used, address failed
     * <br/>  -30      15  Cannot code output address
     * <br/>  -30      16  Apartment number missing or not found in database, and
     * <br/>               an apartment-level match was required.
     * <br/>  -30      17  Probable correctness was less than the minimum
     * <br/>               specified in position 18 of the CONFIG parameter
     * <br/>               record.
     * <br/>  -30      18  PO Box identifier not found or out of range.
     * <br/>  -30      19  Route service identifier not found or out of range.
     * <br/>
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    validateAddress(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/address/validate'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Determines whether or not the given SIC code represents an LTL (formerly Con-way Freight Corp.) location. Returns true if it is.  Validates only if it is LTL (CNWY_CORP_CD = 7200) and a physical service center with dock (FRTOPNS_TYP = �S�).
     * <br/>The purpose is to validate any SIC code entered into any one of the conversation event forms in OEMA.  The original SIC code comes from the Ops023/Ops030 MSTR report, but once inside OEMA, the user can change that SIC code to any other location.  When they do, all we need to ensure is that it is a valid SIC code for LTL (i.e. the old Conway corp code).
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. Valid SIC.
     * <br/>
     * <br/>Post-condtions:
     * <br/>1. Returns true if it is an LTL Corp SIC.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    validateLtlFreightOperationsSic(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/ltl-corp/{sicCd}'), {
            pathParams: request
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Returns location reference information about a service center.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getLocReferenceDetailsBySic(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/loc-reference-sics/{sicCd}'), {
            pathParams: request
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Returns the zone and satellite details for a specific SIC location.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. The SIC code is required.  The Boolean fields are optional.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If the data provided is valid, info about the zone and satellite will be returned.
     * <br/>2. Otherwise an appropriate error message will be returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getZoneAndSatelliteBySic(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/zone-satellite/{sicCd}'), {
            pathParams: request,
            queryParams: request
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Determines if Pickup and Delivery business is running for the SIC on the date provided.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Valid SIC and date are provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. A true value is returned if PnD service is available on the date specified.
     * <br/>2. False is returned if it isn't available.
     * <br/>3. If the data provided is invalid, an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    isPndAvailable(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/pnd-availability/{destSic}/{estimatedDeliveryDt}'), {
            pathParams: request
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Returns SIC that services the input Postal Code.  Input may contain one or more Postal Codes and the corresponding SIC for each input Postal Code will be returned.
     * <br/>
     * <br/>Pre-condition:
     * <br/>One or more Postal Code values are supplied as input. If Active Flag is not set, it defaults to false.
     * <br/>
     * <br/>Post-condition:
     * <br/>The SIC that services each input Postal Code is returned.  If input Active Flag is set to true, only active service centers are returned.  If Active Flag is set to false (default), active and inactive service centers are returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listSicByPostalCd(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/postalassignments'), {
            queryParams: request
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * @return {?}
     */
    getEndPoint() {
        return this.configManager.getSetting(LocationApiService.LocationApiEndpoint);
    }
}
LocationApiService.ɵfac = function LocationApiService_Factory(t) { return new (t || LocationApiService)(ɵngcc0.ɵɵinject(ɵngcc1.DataApiService), ɵngcc0.ɵɵinject(ɵngcc2.ConfigManagerService)); };
LocationApiService.ɵprov = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjectable({ token: LocationApiService, factory: LocationApiService.ɵfac });
LocationApiService.LocationApiEndpoint = 'locationApiEndpoint';
/** @nocollapse */
LocationApiService.ctorParameters = () => [
    { type: DataApiService },
    { type: ConfigManagerService }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(LocationApiService, [{
        type: Injectable
    }], function () { return [{ type: ɵngcc1.DataApiService }, { type: ɵngcc2.ConfigManagerService }]; }, null); })();

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes} checked by tsc
 */
const ɵ0 = { configFileUrl: './assets/config.json' };
class SdkLocationModule {
}
SdkLocationModule.ɵfac = function SdkLocationModule_Factory(t) { return new (t || SdkLocationModule)(); };
SdkLocationModule.ɵmod = /*@__PURE__*/ ɵngcc0.ɵɵdefineNgModule({ type: SdkLocationModule });
SdkLocationModule.ɵinj = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjector({ providers: [
        LocationApiService,
        { provide: CONFIG_MANAGER_SERVICE_CONFIGURATION, useValue: ɵ0 },
    ], imports: [[
            ConfigManagerModule
        ]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(SdkLocationModule, [{
        type: NgModule,
        args: [{
                imports: [
                    ConfigManagerModule
                ],
                providers: [
                    LocationApiService,
                    { provide: CONFIG_MANAGER_SERVICE_CONFIGURATION, useValue: ɵ0 },
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(SdkLocationModule, { imports: function () { return [ConfigManagerModule]; } }); })();

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes} checked by tsc
 */
/**
 * Request to retrieve detailed information for a service center location.
 */
class GetServiceCenterDetailsRqst {
    constructor() {
        /**
         * Zipcode to identify the service center
         */
        this.zipCd = undefined;
    }
}
/**
 * Request to retrieve detailed information for a service center location.
 */
class GetServiceCenterDetailsBySicRqst {
    constructor() {
        /**
         * The code identifying a given service center
         */
        this.sicCd = undefined;
    }
}
/**
 * Response that contains detailed information for a service center location.
 */
class GetServiceCenterDetailsResp {
    constructor() {
        /**
         * The code identifying a given service center
         */
        this.sicCd = undefined;
        /**
         * The code identifying the component for the sic
         */
        this.componentCd = undefined;
        /**
         * address of service center
         */
        this.address = undefined;
        /**
         * GIS lat long
         */
        this.coordinates = undefined;
        /**
         * The offset of the timezone.
         */
        this.timzoneOffset = undefined;
    }
}
/**
 * Response that contains detailed information for a service center location.
 */
class GetServiceCenterDetailsBySicResp {
    constructor() {
        /**
         * The code identifying a given service center
         */
        this.sicCd = undefined;
        /**
         * The code identifying the component for the sic
         */
        this.componentCd = undefined;
        /**
         * address of service center
         */
        this.address = undefined;
        /**
         * GIS lat long
         */
        this.coordinates = undefined;
        /**
         * The name of the timezone.
         */
        this.timezoneName = undefined;
    }
}
/**
 * Request to retrieve the list of sics that have postal assignment changes
 */
class ListSicsWithPostalAssignmentChangesRqst {
    constructor() {
        /**
         * Zipcode to identify the service center
         */
        this.effectiveDate = undefined;
    }
}
/**
 * Response contains the list of sics that have postal assignment changes
 */
class ListSicsWithPostalAssignmentChangesResp {
    constructor() {
        /**
         * The code identifying a given service center
         */
        this.sic = undefined;
    }
}
/**
 * Request to retrieve the list of postal code assignments associated with the sic
 */
class ListSicsRqst {
    constructor() {
        /**
         * sic code
         */
        this.sic = undefined;
        /**
         * name or partial name describing the sic
         */
        this.partialName = undefined;
        this.cityName = undefined;
        this.stateCd = undefined;
        this.countryCd = undefined;
        this.postalCd = undefined;
        this.listInfo = undefined;
    }
}
/**
 * Response contains the list of postal code assignments associated with the sic
 */
class ListSicsResp {
    constructor() {
        /**
         * The sics that match the input search criteria
         */
        this.sics = undefined;
        this.listInfo = undefined;
    }
}
/**
 * Request to retrieve the list of postal code assignments associated with the sic
 */
class ListPostalAssignmentsForSicRqst {
    constructor() {
        /**
         * Zipcode to identify the service center
         */
        this.sic = undefined;
    }
}
/**
 * Response contains the list of postal code assignments associated with the sic
 */
class ListPostalAssignmentsForSicResp {
    constructor() {
        /**
         * The postal code assignments for the sic
         */
        this.postalCodes = undefined;
    }
}
/**
 * Request to retrieve detailed information for a service center location.
 */
class GetSicByFinanceCodesRqst {
    constructor() {
        /**
         * The Oracle Company Code.
         */
        this.companyCd = undefined;
        /**
         * The Oracle Profit Center Code.
         */
        this.profitCenterCd = undefined;
        /**
         * The Oracle Department Code.
         */
        this.departmentCd = undefined;
    }
}
/**
 * Response that contains detailed information for a service center location.
 */
class GetSicByFinanceCodesResp {
    constructor() {
        /**
         * The code for an area, division, district, or service center.
         */
        this.sicCd = undefined;
        /**
         * The name for an area, division, district, or service center.
         */
        this.sicName = undefined;
    }
}
/**
 * Request to find if a given day is a working day or non-working day
 */
class ListOperationalCalendarDaysRqst {
    constructor() {
        this.startDate = undefined;
        this.endDate = undefined;
        /**
         * R(Revenue), W(Weekend), H(Holiday)
         */
        this.revenueDayCode = undefined;
        /**
         * For Revenue use 1.0 and 0 for Weekend and Holiday
         */
        this.revenueAllocationNbrStr = undefined;
        /**
         * pagination information to retrieve list
         */
        this.listInfo = undefined;
    }
}
class ListOperationalCalendarDaysResp {
    constructor() {
        this.opsCalendarDays = undefined;
        this.listInfo = undefined;
    }
}
/**
 * Request to find the next business day based on input date and offset number of days
 */
class GetNextBusinessDayRqst {
    constructor() {
        /**
         * input date
         */
        this.startDate = undefined;
        /**
         * Number of days to add to input date
         */
        this.offsetDays = undefined;
        /**
         * Next business date after adding offset days to start date
         */
        this.nextBusinessDay = undefined;
    }
}
class GetNextBusinessDayResp {
    constructor() {
        /**
         * Next business date after adding offset days to start date
         */
        this.nextBusinessDay = undefined;
        /**
         * input date
         */
        this.startDate = undefined;
        /**
         * Number of days to add to input date
         */
        this.offsetDays = undefined;
    }
}
/**
 * Request to retrieve detailed information for a service center location.
 */
class GetCustomerServiceCenterDetailsRqst {
    constructor() {
        /**
         * The zip code for a service center.
         */
        this.postalCd = undefined;
        /**
         * The unique ID for a service center.
         */
        this.sicCd = undefined;
    }
}
/**
 * Response that contains detailed information for a service center location.
 */
class GetCustomerServiceCenterDetailsResp {
    constructor() {
        /**
         * Details about the service center
         */
        this.serviceCenter = undefined;
    }
}
/**
 * Request message to validate a location address. Address is validated against a vendor provided software.
 */
class ValidateAddressRqst {
    constructor() {
        this.address = undefined;
    }
}
/**
 * Request message to validate a location address. Address is validated against a vendor provided software.
 */
class ValidateAddressResp {
    constructor() {
        this.address = undefined;
        /**
         * Boolean (True/false) field indicating if any validation errors were found during validation. More details will be in the error message section
         */
        this.isValid = undefined;
        this.dataValidationError = undefined;
    }
}
/**
 * Request to retrieve the SIC associated with each Postal Code.
 */
class ListSicByPostalCdRqst {
    constructor() {
        /**
         * Postal Code used to determine the service center
         */
        this.postalCodes = undefined;
        /**
         * If set to true returns only active service centers.  False returns active and inactive service centers  The Default is false.
         */
        this.activeFlag = undefined;
    }
}
/**
 * Response containing the SIC associated with each Postal Code.
 */
class ListSicByPostalCdResp {
    constructor() {
        /**
         * List with SIC for each input postal code.
         */
        this.locFreightPostalAreas = undefined;
        this.validationErrors = undefined;
    }
}
/**
 * Request to validate that a SIC is an LTL one.
 */
class ValidateLtlFreightOperationsSicRqst {
    constructor() {
        /**
         * The SIC code for a service center.
         */
        this.sicCd = undefined;
    }
}
/**
 * Response to the request to validate that a SIC is an LTL one.
 */
class ValidateLtlFreightOperationsSicResp {
    constructor() {
        /**
         * True if SIC is LTL (Con-way Freight Corp); false, otherwise.
         */
        this.isLltlCorpSic = undefined;
    }
}
/**
 * Request to retrieve location reference details for a service center location.
 */
class GetLocReferenceDetailsBySicRqst {
    constructor() {
        /**
         * The code identifying a given service center
         */
        this.sicCd = undefined;
    }
}
/**
 * Response that contains location reference detail information for a service center location.
 */
class GetLocReferenceDetailsBySicResp {
    constructor() {
        this.locReference = undefined;
        this.locCompanyOperations = undefined;
        this.locConWayCorporation = undefined;
        this.chargeToSicCd = undefined;
        this.chargeToSicNm = undefined;
        this.areaSicCd = undefined;
        this.regionSicCd = undefined;
        this.satelliteParentSicCd = undefined;
        this.satelliteParentSicNm = undefined;
        this.zoneParentSicCd = undefined;
        this.zoneParentSicNm = undefined;
        this.linehaulHostSicCd = undefined;
        this.linehaulHostSicNm = undefined;
        this.regionLocHierarchyGroup = undefined;
        this.areaLocHierarchyGroup = undefined;
    }
}
/**
 * Request to return zone and satellite details for a specific SIC location.
 */
class GetZoneAndSatelliteBySicRqst {
    constructor() {
        /**
         * The SIC code for a service center.
         */
        this.sicCd = undefined;
        /**
         * The true/false indicator denoting if the zone should be returned.
         */
        this.zoneInd = undefined;
        /**
         * The true/false indicator denoting if the satellite should be returned.
         */
        this.satelliteInd = undefined;
    }
}
/**
 * Response to the request to return zone and satellite details for a specific SIC location.
 */
class GetZoneAndSatelliteBySicResp {
    constructor() {
        /**
         * The SIC code for a service center.
         */
        this.zoneSatelliteInfo = undefined;
        /**
         * The SIC used in the search (same as the input SIC).
         */
        this.refSic = undefined;
        /**
         * The region for the import SIC (host region for the sic).
         */
        this.refRegionSic = undefined;
        /**
         * The host of the import SIC if the import SIC was a zone.
         */
        this.refZoneSic = undefined;
        /**
         * The host of the import SIC if the import SIC was a satellite.
         */
        this.refSatelliteSic = undefined;
    }
}
/**
 * Request to determine if Pickup and Delivery business is running for the SIC on the date provided.
 */
class IsPndAvailableRqst {
    constructor() {
        /**
         * The SIC that is being checked for PnD availability on the date requested.
         */
        this.destSic = undefined;
        /**
         * The date requested for PnD availability.
         */
        this.estimatedDeliveryDt = undefined;
    }
}
/**
 * Response to the request to determine if Pickup and Delivery business is running for the SIC on the date provided.
 */
class IsPndAvailableResp {
    constructor() {
        /**
         * The date requested for PnD availability.
         */
        this.isAvailable = undefined;
    }
}
/**
 * Information about Operational days for US and CA
 */
class OpsCalendarDay {
    constructor() {
        this.calDate = undefined;
        /**
         * R=Work Day, W=Weekend, H= Holiday
         */
        this.revenueDayCode = undefined;
        /**
         * 1.0 for R, 0 for W and H
         */
        this.revenueAllocationNumber = undefined;
        /**
         * If true then the date is a non-work day for Canada. If false then its a work day
         */
        this.caNonWorkDayInd = undefined;
        /**
         * If true then the date is a non-work day for US. If false then its a work day
         */
        this.usNonWorkDayInd = undefined;
        this.financialReportDate = undefined;
    }
}
/**
 * Information about the Service Center
 */
class ServiceCenter {
    constructor() {
        /**
         * The SIC that provides service to the area identified by the provided zip code.
         */
        this.sicCd = undefined;
        /**
         * The address of the service center.
         */
        this.address = undefined;
        /**
         * The GIS coordinates of the service center.
         */
        this.coordinates = undefined;
        /**
         * The phone number of customer service at the service center.
         */
        this.custServicePhoneNbr = undefined;
        /**
         * The toll-free phone number for the service center.
         */
        this.tollFreePhoneNbr = undefined;
        /**
         * The fax telephone number for the service center.
         */
        this.faxNbr = undefined;
        /**
         * Contains the names and contact information for staff at the service center.
         */
        this.contact = undefined;
    }
}
/**
 * Basic contact information about customers.
 */
class CustomerContactInfoBasic {
    constructor() {
        /**
         * The job title of the contact.
         */
        this.contactTitle = undefined;
        /**
         * The full name of the contact.
         */
        this.contactFullName = undefined;
        /**
         * The email address which is used to communicate with the contact.
         */
        this.email = undefined;
    }
}
/**
 *
 */
class LocCompanyOperations {
    constructor() {
        /**
         *
         */
        this.refSicCd = undefined;
        /**
         *
         */
        this.rsiOperationsTypeCd = undefined;
        /**
         *
         */
        this.truckloadOperationsTypeCd = undefined;
        /**
         *
         */
        this.menloOperationsTypeCd = undefined;
        /**
         *
         */
        this.hostRefSicCd = undefined;
        /**
         *
         */
        this.companyNmCd = undefined;
        /**
         *
         */
        this.classSizeCd = undefined;
        /**
         *
         */
        this.dependentFreightTypeCd = undefined;
        /**
         *
         */
        this.facHoursOfOperationCd = undefined;
        /**
         *
         */
        this.freightOperationsTypeCd = undefined;
        /**
         *
         */
        this.freightServiceAreaTypeCd = undefined;
        /**
         *
         */
        this.intermodalTypeCd = undefined;
        /**
         *
         */
        this.safetyClassSizeCd = undefined;
        /**
         *
         */
        this.satelliteTypeCd = undefined;
        /**
         *
         */
        this.serviceSatelliteTypeCd = undefined;
        /**
         *
         */
        this.maintenanceTypeCd = undefined;
        /**
         *
         */
        this.workDoorCnt = undefined;
        /**
         *
         */
        this.cityOperationsInd = undefined;
        /**
         *
         */
        this.operations24HourInd = undefined;
        /**
         *
         */
        this.documentImageInd = undefined;
        /**
         *
         */
        this.dispatchToInd = undefined;
        /**
         *
         */
        this.dependentSicInd = undefined;
        /**
         *
         */
        this.facInd = undefined;
        /**
         *
         */
        this.freightOperationsInd = undefined;
        /**
         *
         */
        this.freightServiceAreaInd = undefined;
        /**
         *
         */
        this.fuelInd = undefined;
        /**
         *
         */
        this.hostedSicInd = undefined;
        /**
         *
         */
        this.loadToInd = undefined;
        /**
         *
         */
        this.liftInd = undefined;
        /**
         *
         */
        this.linehaulDayMeetInd = undefined;
        /**
         *
         */
        this.linehaulNightMeetInd = undefined;
        /**
         *
         */
        this.rrSpurInd = undefined;
        /**
         *
         */
        this.shipmentOriginDestinationInd = undefined;
        /**
         *
         */
        this.terminalProdInd = undefined;
        /**
         *
         */
        this.terminalStmsLocInd = undefined;
        /**
         *
         */
        this.tieInd = undefined;
        /**
         *
         */
        this.warmRoomInd = undefined;
        /**
         *
         */
        this.yardMeetAndTurnInd = undefined;
        /**
         *
         */
        this.freightLtlMovementInd = undefined;
        /**
         *
         */
        this.freightLtlMaintenanceInd = undefined;
        /**
         *
         */
        this.directions = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class LocConwayCorporation {
    constructor() {
        /**
         *
         */
        this.conwayCorpCd = undefined;
        /**
         *
         */
        this.printConwayCorpCd = undefined;
        /**
         *
         */
        this.corpNm = undefined;
        /**
         *
         */
        this.altCorpCd = undefined;
        /**
         *
         */
        this.componentCd = undefined;
        /**
         *
         */
        this.componentNm = undefined;
        /**
         *
         */
        this.countryCd = undefined;
        /**
         *
         */
        this.companyLogoCd = undefined;
        /**
         *
         */
        this.glCorpCd = undefined;
        /**
         *
         */
        this.scacCd = undefined;
        /**
         *
         */
        this.sltCorp3CharNm = undefined;
        /**
         *
         */
        this.sobCd = undefined;
        /**
         *
         */
        this.companyNmCd = undefined;
        /**
         *
         */
        this.sltT60MajCorpCd = undefined;
        /**
         * Unique identifier
         */
        this.instanceId = undefined;
        /**
         * Active Indicator
         */
        this.activeInd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class LocHierarchyGroup {
    constructor() {
        /**
         *
         */
        this.hierarchyGroupRefSicCd = undefined;
        /**
         *
         */
        this.hierarchyGroupName = undefined;
        /**
         *
         */
        this.hierarchyCategoryCd = undefined;
        /**
         *
         */
        this.sltAreaCd = undefined;
        /**
         *
         */
        this.sltNameA = undefined;
        /**
         *
         */
        this.sltNameB = undefined;
        /**
         *
         */
        this.sltRegionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class LocReference {
    constructor() {
        /**
         * The SIC belongs to a Region/Division which belongs to an Area. This is the Area of the Region to which the SICis associated. It is denormed from the Hierarchy tables.
         */
        this.areaSicCd = undefined;
        /**
         * The SIC that handles charges for this SIC.
         */
        this.chargeRefSicCd = undefined;
        /**
         * The Corporation code for this SIC.
         */
        this.conwayCorpCd = undefined;
        /**
         * ERP Company code for this SIC.
         */
        this.erpCompanyCd = undefined;
        /**
         * ERP Department code for this SIC.
         */
        this.erpDepartmentCd = undefined;
        /**
         * ERP Profit Center code for this SIC.
         */
        this.erpProfitCenterCd = undefined;
        /**
         * The identifier for a Function.Examples:00 - Balance Sheet02 - Linehaul Operations03 - Domestic Operations98 - TaxSource:Slt_Tbl504.M07_Oracle_Function
         */
        this.finFunctionCd = undefined;
        /**
         * Second Function Code included for Standard Location Table requirements.
         */
        this.finSecondFunctionCd = undefined;
        /**
         * SIC code where mail is delivered for this SIC.
         */
        this.mailRefSicCd = undefined;
        /**
         * Code for the mail stop for this SIC which may be an Address that is a Physical Site rather than a Post Office Box
         */
        this.mailStopCd = undefined;
        /**
         * MRC (Management Report Code) is used by some of the older systems for grouping sics together for report purposes.
         */
        this.mrcCd = undefined;
        /**
         * Oracle Area Division SIC cd.
         */
        this.oracleAreaDivisionSicCd = undefined;
        /**
         * Indicates whether the facility is owned or leased. Value Meaning O Owned  L Leased blank Indeterminate or it doesn't matter (null) ZZZ unassigned Sic
         */
        this.ownOrLeaseCd = undefined;
        /**
         * Type of LocationValue MeaningN Non Physical LocationP Physical Location
         */
        this.physicalLocationTypeCd = undefined;
        /**
         * Reference SIC cd identifying this SIC.
         */
        this.refSicCd = undefined;
        /**
         * This defines the high level SIC type:O = Company OperationsD = AdministrationA = Accounting
         */
        this.refSicScpTypeCd = undefined;
        /**
         * Region code for this SIC.
         */
        this.regionSicCd = undefined;
        /**
         * A code used by Safety to record information abouta Freight Service Center.
         */
        this.safetyClassSizeCd = undefined;
        /**
         * Identifies the location that handles scanning for this SIC.
         */
        this.scanLocationSicCd = undefined;
        /**
         * The name assigned to this SIC
         */
        this.sicNm = undefined;
        /**
         * The terminal's two character state abbreviation.
         */
        this.sltStateAbbreviation = undefined;
        /**
         * Short version of the terminal name from the SLT table.
         */
        this.sltTerminalAbbreviation = undefined;
        /**
         * Name of the timezone the sic follows.
         */
        this.timezoneName = undefined;
        /**
         * Category code used by truckload
         */
        this.truckloadCategoryCd = undefined;
        /**
         * Legacy Account Number used by truckload. This field gives them the ability to tie a SIC to an Account.
         */
        this.truckloadXrefCd = undefined;
        /**
         * Two digit country code from the standard location table
         */
        this.sltCountryCd = undefined;
        /**
         * A composite field used for sorting
         */
        this.sltPrimarySequence = undefined;
        /**
         * From the Standard Location Table - Indicates thatthe Location is a Corporate Office.
         */
        this.corportateOfficeInd = undefined;
        /**
         * Daylight Savings Indicator.  Indicates whether the location observes daylight savings time or not.
         */
        this.daylightSavingsTimeInd = undefined;
        /**
         * Indicates that the Sic is a Book Closing Locationand is referenced in the Oracle Financial system.
         */
        this.finSicInd = undefined;
        /**
         * This Reference is used to group other sics intosome kind of a hierarchical group.
         */
        this.hierarchyGroupInd = undefined;
        /**
         * Indicates that the Kronos system is used to keeptrack of hours worked at the location. Normallythis will be for a city operations center or afreight assembly center.It may be a warehouse in the future.
         */
        this.kronosInd = undefined;
        /**
         * SIC is used for mileage tracking purposes
         */
        this.mileageInd = undefined;
        /**
         * Indicates that the Operations Begin Date and theOperations End Date as well as information aboutthe Sic itself comprise privileged information.
         */
        this.privateInd = undefined;
        /**
         * Identifies a Location that can handle scanning for other Locations
         */
        this.scanLocationInd = undefined;
        /**
         * Indicates whether this SIC is used for sales
         */
        this.salesSicInd = undefined;
        /**
         * Unique identifier
         */
        this.instanceId = undefined;
        /**
         * Effective date of the location reference
         */
        this.effDate = undefined;
        /**
         * Expiration date of the location reference
         */
        this.exprDate = undefined;
        /**
         * The date that operations actually begins for somelocations. This is different from the EffectiveDate of the Reference.
         */
        this.operationsBeginDate = undefined;
        /**
         * The date that operations ends for some locations.This is different from the Expiration Date of theReference.
         */
        this.operationsEndDate = undefined;
        /**
         * If there is a lease, this is its expiration date.If the Own Or Lease Code is blank or 'O' the dateis probably arbitrary.
         */
        this.leaseExprDt = undefined;
        /**
         * Identifies the date when a SIC became, or will become, a SCAN SIC and starts performing that function for other SICs.
         */
        this.scanDate = undefined;
        /**
         * Identifier of the location where mal is delivered for this SIC.
         */
        this.mailAddressId = undefined;
        /**
         * Time offset from Portland time
         */
        this.pdtTimeOffsetNbr = undefined;
        /**
         * Indicates whether time offset is needed from Pacific time
         */
        this.pdtTimeOffsetInd = undefined;
        /**
         * Denormed from foreign key PHYS_SITE_LATD on GEO_ADDRESS from relationship to GEO_PLACE.
         */
        this.physicalSiteLatitude = undefined;
        /**
         * Denormed from foreign key PHYS_SITE_LNGT on GEO_ADDRESS from relationship to GEO_PLACE.
         */
        this.physicalSiteLongitude = undefined;
        /**
         * The date this location began accepting mail for other locations.
         */
        this.mailLocationAddressEffDate = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         */
        this.auditInfo = undefined;
    }
}
/**
 * Details about zone and satellite.
 */
class ZoneSatelliteInfo {
    constructor() {
        /**
         * The SIC code for the region.
         */
        this.regionSic = undefined;
        /**
         * The SIC code for the location.
         */
        this.locationSic = undefined;
        /**
         * The parent satellite.
         */
        this.satelliteParent = undefined;
        /**
         * The zone location of the terminal.
         */
        this.zoneTerminal = undefined;
    }
}
/**
 *
 */
class LocFreightPostalArea {
    constructor() {
        /**
         * The SIC that provides service to the area identified by the provided zip code.
         */
        this.sicCd = undefined;
        /**
         * Unique identifier for a Country.
         */
        this.countryCd = undefined;
        /**
         * The identifier that distinguishes one Country Subdivision from another within a Country. This represents the ISO standard code for a CountrySubdivision
         */
        this.countrySubdivisionCd = undefined;
        /**
         * The identifier for the Geographic Postal Areathat is included as part of the Freight Service Area.
         */
        this.geoPostalCd = undefined;
        /**
         * Indicates whether this Geographic Postal Area isan active part of this Freight Service Area.
         */
        this.activeFlag = undefined;
        /**
         * Some Postal Areas do not get direct service dueto distance or inacessability. Freight to Customers in these areascannot have guaranteed delivery because there may not be daily schedules to indirect postal areas.  These areas are normally served by interlinecarriers.
         */
        this.dirFlag = undefined;
        /**
         * The last day that the link between GeographicPostal Area and Freight Service Area is active.
         */
        this.expirationDt = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         */
        this.auditInfo = undefined;
    }
}
class ValidateAddressResp_DataValidationError extends DataValidationError {
    constructor() {
        super(...arguments);
        this.errorReasonCd = undefined;
    }
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes} checked by tsc
 */

export { SdkLocationModule, LocationApiService, GetServiceCenterDetailsRqst, GetServiceCenterDetailsBySicRqst, GetServiceCenterDetailsResp, GetServiceCenterDetailsBySicResp, ListSicsWithPostalAssignmentChangesRqst, ListSicsWithPostalAssignmentChangesResp, ListSicsRqst, ListSicsResp, ListPostalAssignmentsForSicRqst, ListPostalAssignmentsForSicResp, GetSicByFinanceCodesRqst, GetSicByFinanceCodesResp, ListOperationalCalendarDaysRqst, ListOperationalCalendarDaysResp, GetNextBusinessDayRqst, GetNextBusinessDayResp, GetCustomerServiceCenterDetailsRqst, GetCustomerServiceCenterDetailsResp, ValidateAddressRqst, ValidateAddressResp, ListSicByPostalCdRqst, ListSicByPostalCdResp, ValidateLtlFreightOperationsSicRqst, ValidateLtlFreightOperationsSicResp, GetLocReferenceDetailsBySicRqst, GetLocReferenceDetailsBySicResp, GetZoneAndSatelliteBySicRqst, GetZoneAndSatelliteBySicResp, IsPndAvailableRqst, IsPndAvailableResp, OpsCalendarDay, ServiceCenter, CustomerContactInfoBasic, LocCompanyOperations, LocConwayCorporation, LocHierarchyGroup, LocReference, ZoneSatelliteInfo, LocFreightPostalArea, ValidateAddressResp_DataValidationError };

